import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LogOut, CalendarDays, Image as ImageIcon, Users,
  UserSquare2, Megaphone, FileText, Newspaper, Menu as NavIcon,
  FilePlus, HelpCircle, CalendarClock, UserPlus, Briefcase,
} from "lucide-react";

const navItem =
  "flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors text-[color:var(--ink-soft)] hover:text-[color:var(--blue-deep)] hover:bg-[color:var(--cream)]";
const navItemActive =
  "bg-[color:var(--blue)] text-white hover:text-white hover:bg-[color:var(--blue-deep)]";

export default function AdminLayout() {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-[color:var(--cream)]" data-testid="admin-layout">
      <header className="sticky top-0 z-30 bg-white border-b border-[color:var(--line)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="font-display text-xl text-[color:var(--blue-ink)]"
              data-testid="admin-home-link"
            >
              TIOB Admin
            </Link>
            <span className="text-xs uppercase tracking-widest text-[color:var(--muted)] hidden md:inline">
              Temple Israel of Brevard
            </span>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <span className="text-sm text-[color:var(--ink-soft)] hidden md:inline">
                {user.email}
              </span>
            )}
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-full border border-[color:var(--line)] text-[color:var(--ink-soft)] hover:bg-[color:var(--cream)] transition"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
        <aside className="md:sticky md:top-24 self-start space-y-1">
          <div className="px-4 pt-1 pb-2 text-[10px] uppercase tracking-widest text-[color:var(--muted)] font-mono-accent">Content</div>
          <NavLink to="/admin/events" data-testid="admin-nav-events"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <CalendarDays className="w-4 h-4" /> Events
          </NavLink>
          <NavLink to="/admin/weekly" data-testid="admin-nav-weekly"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <CalendarClock className="w-4 h-4" /> Weekly Calendar
          </NavLink>
          <NavLink to="/admin/membership" data-testid="admin-nav-membership"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <UserPlus className="w-4 h-4" /> Applications
          </NavLink>
          <NavLink to="/admin/hiring" data-testid="admin-nav-hiring"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <Briefcase className="w-4 h-4" /> Job Applications
          </NavLink>
          <NavLink to="/admin/staff" data-testid="admin-nav-staff"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <UserSquare2 className="w-4 h-4" /> Staff &amp; Board
          </NavLink>
          <NavLink to="/admin/pages" data-testid="admin-nav-pages"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <FilePlus className="w-4 h-4" /> Pages
          </NavLink>
          <NavLink to="/admin/content" data-testid="admin-nav-content"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <FileText className="w-4 h-4" /> Page Copy
          </NavLink>
          <NavLink to="/admin/news" data-testid="admin-nav-news"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <Newspaper className="w-4 h-4" /> News
          </NavLink>
          <NavLink to="/admin/announcements" data-testid="admin-nav-announcements"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <Megaphone className="w-4 h-4" /> Announcements
          </NavLink>
          <NavLink to="/admin/media" data-testid="admin-nav-media"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <ImageIcon className="w-4 h-4" /> Media Library
          </NavLink>

          <div className="px-4 pt-4 pb-2 text-[10px] uppercase tracking-widest text-[color:var(--muted)] font-mono-accent">Site</div>
          <NavLink to="/admin/navigation" data-testid="admin-nav-navigation"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <NavIcon className="w-4 h-4" /> Navigation Menu
          </NavLink>
          <NavLink to="/admin/users" data-testid="admin-nav-users"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <Users className="w-4 h-4" /> Staff Accounts
          </NavLink>
          <NavLink to="/admin/help" data-testid="admin-nav-help"
            className={({ isActive }) => `${navItem} ${isActive ? navItemActive : ""}`}>
            <HelpCircle className="w-4 h-4" /> Help &amp; Tutorial
          </NavLink>
        </aside>

        <main className="bg-white rounded-2xl border border-[color:var(--line)] p-6 md:p-8 min-h-[60vh]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
